import React from 'react';
import PropTypes from 'prop-types';

/**
 * SectionHeader Component
 * 
 * Consistent section headers within pages.
 * Used for "Quick Actions", "Active Sessions", etc.
 * 
 * Usage:
 * <SectionHeader icon="🔴" title="Aktive Sitzungen" />
 * <SectionHeader title="Vorlagen" action={<Button>Neu</Button>} />
 */
export const SectionHeader = ({
  title,
  icon = null,
  subtitle = null,
  action = null,
  size = 'md', // 'sm', 'md', 'lg'
  className = ''
}) => {
  const sizeClasses = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl'
  };

  return (
    <div className={`flex items-center justify-between mb-4 ${className}`}>
      <div>
        <h2 className={`${sizeClasses[size]} font-semibold text-gray-900 dark:text-white flex items-center gap-2`}>
          {icon && <span>{icon}</span>}
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string
};

/**
 * AlertBanner Component
 * 
 * Alert/notification banner for errors, warnings, success messages.
 * 
 * Usage:
 * <AlertBanner variant="danger" message="Ein Fehler ist aufgetreten" />
 * <AlertBanner variant="success" message="Gespeichert!" onDismiss={() => {}} />
 */
export const AlertBanner = ({
  variant = 'info', // 'info', 'success', 'warning', 'danger'
  message,
  title = null,
  onDismiss = null,
  className = ''
}) => {
  const variantClasses = {
    info: 'bg-primary-100 dark:bg-primary-900/30 border-primary-400 text-primary-700 dark:text-primary-300',
    success: 'bg-success-100 dark:bg-success-900/30 border-success-400 text-success-700 dark:text-success-300',
    warning: 'bg-warning-100 dark:bg-warning-900/30 border-warning-400 text-warning-700 dark:text-warning-300',
    danger: 'bg-danger-100 dark:bg-danger-900/30 border-danger-400 text-danger-700 dark:text-danger-300'
  };

  const icons = {
    info: 'ℹ️',
    success: '✅',
    warning: '⚠️',
    danger: '❌'
  };

  return (
    <div className={`border rounded-lg px-4 py-3 ${variantClasses[variant]} ${className}`}>
      <div className="flex items-start gap-3">
        <span className="text-lg flex-shrink-0">{icons[variant]}</span>
        <div className="flex-1">
          {title && <p className="font-semibold mb-1">{title}</p>}
          <p className="text-sm">{message}</p>
        </div>
        {onDismiss && (
          <button 
            onClick={onDismiss}
            className="text-lg opacity-60 hover:opacity-100 transition-opacity"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

AlertBanner.propTypes = {
  variant: PropTypes.oneOf(['info', 'success', 'warning', 'danger']),
  message: PropTypes.string.isRequired,
  title: PropTypes.string,
  onDismiss: PropTypes.func,
  className: PropTypes.string
};

/**
 * Modal Component
 * 
 * Base modal/dialog component.
 * 
 * Usage:
 * <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Settings">
 *   <p>Modal content here</p>
 * </Modal>
 */
export const Modal = ({
  isOpen = false,
  onClose,
  title = null,
  size = 'md', // 'sm', 'md', 'lg', 'xl', 'full'
  children,
  footer = null,
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = ''
}) => {
  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full mx-4'
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div 
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col ${className}`}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
        
        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', '2xl', 'full']),
  children: PropTypes.node,
  footer: PropTypes.node,
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  className: PropTypes.string
};

export default { SectionHeader, AlertBanner, Modal };
